import { useCallback, useState } from 'react'
import { useQuery } from '@apollo/client'
import type { ApolloError, WatchQueryFetchPolicy } from '@apollo/client'
import { IForumTopic, IForumTopicData, IForumTopicsData } from '@dtos/Comment'
import { GET_FORUM_TOPIC, GET_FORUM_TOPICS } from './queries/getForumTopics'
import { dedupeById } from '@utils/dedupeById'

// A implementação mora em utils (o fórum e a lista customizada usam a mesma),
// mas segue reexportada porque `TopicItem` já importava daqui.
export { dedupeById }

const sortByCreatedAtDesc = <T extends { createdAt: string }>(
  items: readonly T[],
): T[] =>
  [...items].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )

const sortByCreatedAtAsc = <T extends { createdAt: string }>(
  items: readonly T[],
): T[] =>
  [...items].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  )

const mergeUniqueById = (
  prev: IForumTopic[],
  next: readonly IForumTopic[],
): IForumTopic[] => {
  const seen = new Set(prev.map((t) => t.id))
  const merged = [...prev]
  next.forEach((t) => {
    if (!seen.has(t.id)) {
      seen.add(t.id)
      merged.push(t)
    }
  })
  return merged
}

interface IGetForumTopicsProps {
  courseContentId: string
  perPage?: number
  skip?: boolean
  /**
   * `cache-and-network` (default) para quem exibe a lista e precisa de dados
   * frescos. Quem só quer a contagem passa `cache-first`: o LessonLayout no
   * desktop monta TabsContentContainer duas vezes (tabs no topo + conteúdo
   * embaixo), e com `cache-and-network` nas duas instâncias a mesma query saía
   * duas vezes na rede — os mounts não coincidem no tempo, então o
   * deduplication do Apollo não pega.
   */
  fetchPolicy?: WatchQueryFetchPolicy
}

interface IGetForumTopicsResult {
  data: IForumTopicsData | undefined
  topics: IForumTopic[]
  totalCount: number
  totalPages: number
  currentPage: number
  commentsEnabled: boolean
  hasMore: boolean
  loadMore: () => Promise<void>
  loadingMore: boolean
  loading: boolean
  error: ApolloError | undefined
  refetch: () => Promise<void>
}

export const useGetForumTopics = ({
  courseContentId,
  perPage = 10,
  skip,
  fetchPolicy = 'cache-and-network',
}: IGetForumTopicsProps): IGetForumTopicsResult => {
  const [accumulated, setAccumulated] = useState<IForumTopic[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [commentsEnabled, setCommentsEnabled] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const { data, loading, error, refetch, fetchMore } =
    useQuery<IForumTopicsData>(GET_FORUM_TOPICS, {
      variables: { courseContentId, page: 1, perPage },
      skip: skip || !courseContentId,
      fetchPolicy,
      notifyOnNetworkStatusChange: true,
      onCompleted: (result) => {
        const ft = result?.forumTopics
        if (!ft) return
        setAccumulated(sortByCreatedAtDesc(dedupeById(ft.topics ?? [])))
        setCurrentPage(ft.currentPage ?? 1)
        setTotalPages(ft.totalPages ?? 1)
        setTotalCount(ft.totalCount ?? 0)
        setCommentsEnabled(ft.commentsEnabled ?? false)
      },
    })

  const loadMore = useCallback(async () => {
    if (loadingMore || loading) return
    if (currentPage >= totalPages) return
    const nextPage = currentPage + 1
    setLoadingMore(true)
    try {
      const result = await fetchMore({
        variables: { courseContentId, page: nextPage, perPage },
      })
      const ft = result.data?.forumTopics
      if (!ft) return
      setAccumulated((prev) =>
        sortByCreatedAtDesc(mergeUniqueById(prev, ft.topics ?? [])),
      )
      setCurrentPage(ft.currentPage ?? nextPage)
      setTotalPages(ft.totalPages ?? totalPages)
      setTotalCount(ft.totalCount ?? totalCount)
    } finally {
      setLoadingMore(false)
    }
  }, [
    courseContentId,
    currentPage,
    fetchMore,
    loading,
    loadingMore,
    perPage,
    totalCount,
    totalPages,
  ])

  const handleRefetch = useCallback(async () => {
    setAccumulated([])
    setCurrentPage(1)
    await refetch({ courseContentId, page: 1, perPage })
  }, [courseContentId, perPage, refetch])

  return {
    data,
    topics: accumulated,
    totalCount,
    totalPages,
    currentPage,
    commentsEnabled,
    hasMore: currentPage < totalPages,
    loadMore,
    loadingMore,
    loading,
    error,
    refetch: handleRefetch,
  }
}

interface IUseForumTopicsCountProps {
  courseContentId: string | undefined | null
  skip?: boolean
}

interface IUseForumTopicsCountResult {
  count: number
  hasTopics: boolean
  commentsEnabled: boolean
  loading: boolean
}

export const useForumTopicsCount = ({
  courseContentId,
  skip,
}: IUseForumTopicsCountProps): IUseForumTopicsCountResult => {
  const { totalCount, commentsEnabled, loading } = useGetForumTopics({
    courseContentId: courseContentId ?? '',
    skip: skip || !courseContentId,
    // Só a contagem: serve do cache. Quem renderiza a lista (CommentThread)
    // mantém `cache-and-network` e atualiza o cache para todos.
    fetchPolicy: 'cache-first',
  })

  return {
    count: totalCount,
    hasTopics: totalCount > 0,
    commentsEnabled,
    loading,
  }
}

interface IGetForumTopicProps {
  id: string
  postsLimit?: number
  postsOffset?: number
  skip?: boolean
}

interface IGetForumTopicResult {
  data: IForumTopicData | undefined
  loading: boolean
  error: ApolloError | undefined
  refetch: () => void
}

export const useGetForumTopic = ({
  id,
  postsLimit = 20,
  postsOffset = 0,
  skip,
}: IGetForumTopicProps): IGetForumTopicResult => {
  const { data, loading, error, refetch } = useQuery<IForumTopicData>(
    GET_FORUM_TOPIC,
    {
      variables: { id, postsLimit, postsOffset },
      skip: skip || !id,
      fetchPolicy: 'cache-and-network',
    },
  )

  return { data, loading, error, refetch: () => refetch() }
}

export const sortPostsByCreatedAtDesc = sortByCreatedAtDesc
export const sortPostsByCreatedAtAsc = sortByCreatedAtAsc
